import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Container,
  Table,
  Loader,
  Segment,
  Header,
  Label,
  Grid,
  Icon,
} from 'semantic-ui-react';
import { Helmet } from '@plone/volto/helpers';
import { getApiPath, getAuthHeaders, fetchApi } from '../api';

const downloadPo = (platform, language) => {
  const url = `${getApiPath()}/@polyglot/export/po?platform=${encodeURIComponent(platform)}&language=${encodeURIComponent(language)}`;
  fetch(url, { headers: getAuthHeaders() })
    .then((res) => {
      if (!res.ok) throw new Error(`${res.status}`);
      return res.blob();
    })
    .then((blob) => {
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${platform}-${language}.po`;
      a.click();
      URL.revokeObjectURL(a.href);
    })
    .catch((err) => alert(`Export failed: ${err.message}`));
};

const LanguageDetail = () => {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [verification, setVerification] = useState(null);
  const [qcFindings, setQcFindings] = useState(null);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetchApi(`${getApiPath()}/@polyglot/languages/${code}`),
      fetchApi(`${getApiPath()}/@polyglot/verification?language=${code}&page_size=1`)
        .then((json) => json.summary || [])
        .catch(() => []),
      fetchApi(`${getApiPath()}/@polyglot/qc-findings?language=${code}&page_size=5`)
        .then((json) => ({ total: json.items_total || 0, items: (json.items || []).slice(0, 5) }))
        .catch(() => ({ total: 0, items: [] })),
    ])
      .then(([langJson, verifSummary, findingsData]) => {
        setData(langJson);
        setVerification(verifSummary);
        setQcFindings(findingsData);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [code]);

  if (loading) {
    return (
      <Container id="polyglot-language">
        <Loader active inline="centered" content="Loading language data..." />
      </Container>
    );
  }

  if (error) {
    return (
      <Container id="polyglot-language">
        <Segment color="red">Error: {error}</Segment>
      </Container>
    );
  }

  const { coverage, conventions, risk } = data;

  return (
    <Container id="polyglot-language" style={{ paddingTop: '2em', paddingBottom: '2em' }}>
      <Helmet title={`${conventions?.language_name_native || code} - Polyglot`} />
      <Link to="/polyglot">&larr; Back to Dashboard</Link>

      {/* Language header with conventions summary inline */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginTop: '0.5em', marginBottom: '1.5em' }}>
        <div>
          <Header as="h1" style={{ marginBottom: '0.3em' }}>
            {conventions?.language_name_native || code}
          </Header>
          <div style={{ color: '#666', fontSize: '1.1em' }}>
            <span style={{ marginRight: '1.5em' }}><strong>{code}</strong></span>
            {conventions?.script && (
              <span style={{ marginRight: '1.5em' }}>{conventions.script}</span>
            )}
            {conventions?.writing_direction === 'rtl' && (
              <Label size="small" color="blue">RTL</Label>
            )}
          </div>
        </div>
        {conventions && (
          <div style={{ textAlign: 'right', fontSize: '0.95em', color: '#555' }}>
            {conventions.register && (
              <div>Register: <strong>{conventions.register}</strong></div>
            )}
            {conventions.button_style && (
              <div>Button style: <strong>{conventions.button_style}</strong></div>
            )}
            <Link to={`/polyglot/conventions/${code}`} style={{ fontSize: '0.9em' }}>
              Full conventions &rarr;
            </Link>
          </div>
        )}
      </div>

      {/* Coverage + Risk side by side */}
      <Grid stackable columns={2}>
        <Grid.Column>
          <Segment style={{ overflow: 'hidden' }}>
            <Header as="h3">Coverage</Header>
            <Table compact celled size="small" style={{ tableLayout: 'auto' }}>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Platform</Table.HeaderCell>
                  <Table.HeaderCell textAlign="right">Total</Table.HeaderCell>
                  <Table.HeaderCell textAlign="right">Done</Table.HeaderCell>
                  <Table.HeaderCell textAlign="right">Kept</Table.HeaderCell>
                  <Table.HeaderCell textAlign="right">%</Table.HeaderCell>
                  <Table.HeaderCell textAlign="center" style={{ width: '40px' }}>.po</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {(coverage || []).map((row) => (
                  <Table.Row key={row.platform}>
                    <Table.Cell>
                      <Link to={`/polyglot/browse/${row.platform}?language=${code}`}>
                        {row.platform}
                      </Link>
                    </Table.Cell>
                    <Table.Cell textAlign="right">{parseInt(row.translatable || 0, 10).toLocaleString()}</Table.Cell>
                    <Table.Cell textAlign="right">{parseInt(row.translated || 0, 10).toLocaleString()}</Table.Cell>
                    <Table.Cell textAlign="right">{parseInt(row.preserved || 0, 10).toLocaleString()}</Table.Cell>
                    <Table.Cell textAlign="right">
                      <strong>{parseFloat(row.coverage_pct || 0).toFixed(1)}%</strong>
                    </Table.Cell>
                    <Table.Cell textAlign="center">
                      <Icon
                        name="download"
                        link
                        title={`Download ${row.platform}-${code}.po`}
                        style={{ cursor: 'pointer', color: '#2185d0' }}
                        onClick={() => downloadPo(row.platform, code)}
                      />
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </Segment>
        </Grid.Column>

        <Grid.Column>
          {risk && risk.length > 0 && (
            <Segment>
              <Header as="h3">Risk Summary</Header>
              <Table compact celled>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>Platform</Table.HeaderCell>
                    <Table.HeaderCell textAlign="right">Unacceptable</Table.HeaderCell>
                    <Table.HeaderCell textAlign="right">ALARP</Table.HeaderCell>
                    <Table.HeaderCell textAlign="right">Avg RPN</Table.HeaderCell>
                    <Table.HeaderCell textAlign="right">Max RPN</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {risk.map((row, i) => (
                    <Table.Row key={i}>
                      <Table.Cell>{row.platform}</Table.Cell>
                      <Table.Cell textAlign="right">
                        {parseInt(row.unacceptable_count || 0, 10) > 0 ? (
                          <Label color="red" size="small">
                            {parseInt(row.unacceptable_count, 10).toLocaleString()}
                          </Label>
                        ) : (
                          '0'
                        )}
                      </Table.Cell>
                      <Table.Cell textAlign="right">
                        {parseInt(row.alarp_count || 0, 10) > 0 ? (
                          <Label color="yellow" size="small">
                            {parseInt(row.alarp_count, 10).toLocaleString()}
                          </Label>
                        ) : (
                          '0'
                        )}
                      </Table.Cell>
                      <Table.Cell textAlign="right">
                        {parseFloat(row.avg_rpn || 0).toFixed(1)}
                      </Table.Cell>
                      <Table.Cell textAlign="right">{parseInt(row.max_rpn || 0, 10)}</Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </Segment>
          )}
        </Grid.Column>
      </Grid>

      {/* Verification + QC Findings side by side */}
      <Grid stackable columns={2}>
        <Grid.Column>
          {verification && verification.length > 0 && (
            <Segment>
              <Header as="h3">Verification</Header>
              <Table compact celled>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>Platform</Table.HeaderCell>
                    <Table.HeaderCell textAlign="right">Verified</Table.HeaderCell>
                    <Table.HeaderCell textAlign="right">Pass</Table.HeaderCell>
                    <Table.HeaderCell textAlign="right">Conditional</Table.HeaderCell>
                    <Table.HeaderCell textAlign="right">Fail</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {verification.map((row, i) => (
                    <Table.Row key={i}>
                      <Table.Cell>{row.platform}</Table.Cell>
                      <Table.Cell textAlign="right">
                        {parseInt(row.verified || 0, 10)}/{parseInt(row.total_translated || 0, 10)}
                      </Table.Cell>
                      <Table.Cell textAlign="right">
                        {parseInt(row.pass || 0, 10) > 0 ? (
                          <Label color="green" size="small">{parseInt(row.pass, 10)}</Label>
                        ) : '0'}
                      </Table.Cell>
                      <Table.Cell textAlign="right">
                        {parseInt(row.conditional || 0, 10) > 0 ? (
                          <Label color="yellow" size="small">{parseInt(row.conditional, 10)}</Label>
                        ) : '0'}
                      </Table.Cell>
                      <Table.Cell textAlign="right">
                        {parseInt(row.fail || 0, 10) > 0 ? (
                          <Label color="red" size="small">{parseInt(row.fail, 10)}</Label>
                        ) : '0'}
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
              <Link to={`/polyglot/verification/${code}`}>
                View all verified strings &rarr;
              </Link>
            </Segment>
          )}
        </Grid.Column>

        <Grid.Column>
          {qcFindings && qcFindings.total > 0 && (
            <Segment>
              <Header as="h3">
                <Icon name="clipboard check" />
                QC Findings
                <Label color="orange" size="small" style={{ marginLeft: '0.5em' }}>
                  {qcFindings.total} pending
                </Label>
              </Header>
              <Table compact celled size="small">
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>Msg</Table.HeaderCell>
                    <Table.HeaderCell>Platform</Table.HeaderCell>
                    <Table.HeaderCell>Error</Table.HeaderCell>
                    <Table.HeaderCell>Finding</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {qcFindings.items.map((f) => (
                    <Table.Row key={f.id}>
                      <Table.Cell>
                        <Link to={`/polyglot/messages/${f.message_id}`}>
                          {f.message_id}
                        </Link>
                      </Table.Cell>
                      <Table.Cell>{f.platform}</Table.Cell>
                      <Table.Cell>
                        <Label size="mini">{f.error_type_label || f.error_type}</Label>
                      </Table.Cell>
                      <Table.Cell style={{ maxWidth: '300px', wordBreak: 'break-word' }}>
                        {f.finding}
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
              <Link to={`/polyglot/qc/findings?language=${code}`}>
                View all {qcFindings.total} findings &rarr;
              </Link>
            </Segment>
          )}
        </Grid.Column>
      </Grid>

      {/* Notes if present — full width */}
      {conventions?.notes && (
        <Segment style={{ marginTop: '0.5em' }}>
          <Header as="h4">Notes</Header>
          <p style={{ whiteSpace: 'pre-wrap', color: '#444' }}>{conventions.notes}</p>
        </Segment>
      )}
    </Container>
  );
};

export default LanguageDetail;
